const APP_AUTH_CALLBACK_PATH = "/auth/callback";
export const PREVIEW_SIGNIN_CALLBACK_PATH = "/auth/preview-callback";
const PREVIEW_LOGOUT_START_PATH = "/auth/preview-logout/start";

const ROUTE_HINT_PREFIX = "preview-v1.";
const AUTH_RESPONSE_FRAGMENT_PREFIX = "#preview-auth-response=";
const LOGOUT_TARGET_FRAGMENT_PREFIX = "#preview-logout-target=";
const MAX_AUTH_RESPONSE_LENGTH = 8_192;
const OAUTH_RESPONSE_KEYS = new Set([
  "code",
  "error",
  "error_description",
  "error_uri",
  "iss",
  "session_state",
  "state",
]);

export interface PreviewRelayConfig {
  origin: string;
  allowedHostPrefix: string;
  allowedHostSuffix: string;
  logoutEndpoint: string;
}

interface AuthCallbackLocation {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
}

export function parsePreviewRelayConfig(
  value: unknown,
): PreviewRelayConfig | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw invalidRelayConfig();
  }
  const record = value as Record<string, unknown>;
  if (record.enabled === false) return null;
  if (record.enabled !== true) throw invalidRelayConfig();

  const config: PreviewRelayConfig = {
    origin: requiredString(record.origin),
    allowedHostPrefix: requiredString(record.allowedHostPrefix),
    allowedHostSuffix: requiredString(record.allowedHostSuffix),
    logoutEndpoint: requiredString(record.logoutEndpoint),
  };
  try {
    const relay = exactHttpsOrigin(config.origin);
    validateHostPrefix(config.allowedHostPrefix);
    validateHostSuffix(config.allowedHostSuffix);
    exactHttpsEndpoint(config.logoutEndpoint);
    if (isAllowedPreviewHost(relay.hostname, config)) {
      throw invalidRelayConfig();
    }
  } catch {
    throw invalidRelayConfig();
  }
  return config;
}

export function createPreviewRouteHint(
  previewOrigin: string,
  config: PreviewRelayConfig,
): string {
  return `${ROUTE_HINT_PREFIX}${previewHost(previewOrigin, config)}`;
}

export function captureAuthResponseBeforeTelemetry(
  location: AuthCallbackLocation,
  replacePath: (path: string) => void,
): string | null {
  if (location.pathname !== APP_AUTH_CALLBACK_PATH) return null;
  if (!location.search && !location.hash) return null;

  const search = location.search;
  const hash = location.hash;
  replacePath(APP_AUTH_CALLBACK_PATH);

  try {
    if (search && hash) throw new Error("ambiguous callback response");
    let rawQuery: string;
    if (search) {
      rawQuery = validateOAuthResponse(search);
    } else {
      if (!hash.startsWith(AUTH_RESPONSE_FRAGMENT_PREFIX)) {
        throw new Error("unknown callback fragment");
      }
      rawQuery = validateOAuthResponse(
        decodeBase64Url(hash.slice(AUTH_RESPONSE_FRAGMENT_PREFIX.length)),
      );
    }
    return `${location.origin}${APP_AUTH_CALLBACK_PATH}?${rawQuery}`;
  } catch {
    throw new Error("OAuth response is invalid");
  }
}

export function buildPreviewLogoutStartUrl(
  previewOrigin: string,
  config: PreviewRelayConfig,
): string {
  const routeHint = createPreviewRouteHint(previewOrigin, config);
  return `${config.origin}${PREVIEW_LOGOUT_START_PATH}${LOGOUT_TARGET_FRAGMENT_PREFIX}${encodeBase64Url(routeHint)}`;
}

export function buildDirectCognitoLogoutUrl(
  logoutEndpoint: string,
  clientId: string,
  logoutUri: string,
): string {
  if (!clientId.trim()) throw new Error("Cognito client id is missing");
  const url = exactHttpsEndpoint(logoutEndpoint);
  const destination = new URL(logoutUri);
  if (
    destination.protocol !== "https:" ||
    destination.username ||
    destination.password ||
    destination.port ||
    destination.search ||
    destination.hash ||
    `${destination.origin}${destination.pathname === "/" ? "" : destination.pathname}` !==
      logoutUri
  ) {
    throw new Error("Cognito logout URI is invalid");
  }
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("logout_uri", logoutUri);
  return url.toString();
}

function previewHost(origin: string, config: PreviewRelayConfig): string {
  let url: URL;
  try {
    url = exactHttpsOrigin(origin);
  } catch {
    throw new Error("Origin is not an allowed preview origin");
  }
  if (!isAllowedPreviewHost(url.hostname, config)) {
    throw new Error("Origin is not an allowed preview origin");
  }
  return url.hostname;
}

function isAllowedPreviewHost(
  hostname: string,
  config: Pick<PreviewRelayConfig, "allowedHostPrefix" | "allowedHostSuffix">,
): boolean {
  if (hostname !== hostname.toLowerCase()) return false;
  if (!hostname.endsWith(config.allowedHostSuffix)) return false;
  const firstLabel = hostname.slice(0, -config.allowedHostSuffix.length);
  if (!firstLabel.startsWith(config.allowedHostPrefix)) return false;
  const pullRequestNumber = firstLabel.slice(config.allowedHostPrefix.length);
  if (!/^[1-9][0-9]*$/u.test(pullRequestNumber)) return false;
  return dnsLabel(firstLabel);
}

function validateOAuthResponse(search: string): string {
  const rawQuery = search.startsWith("?") ? search.slice(1) : search;
  if (!rawQuery || rawQuery.length > MAX_AUTH_RESPONSE_LENGTH) {
    throw new Error("invalid OAuth response length");
  }
  const params = new URLSearchParams(rawQuery);
  for (const key of params.keys()) {
    if (!OAUTH_RESPONSE_KEYS.has(key)) {
      throw new Error("unknown OAuth response parameter");
    }
  }
  if (params.getAll("state").length !== 1 || !params.get("state")) {
    throw new Error("invalid OAuth state");
  }
  const codeCount = params.getAll("code").filter(Boolean).length;
  const errorCount = params.getAll("error").filter(Boolean).length;
  if (codeCount + errorCount !== 1) {
    throw new Error("invalid OAuth result");
  }
  if (params.getAll("code").length !== codeCount) {
    throw new Error("invalid OAuth code");
  }
  if (params.getAll("error").length !== errorCount) {
    throw new Error("invalid OAuth error");
  }
  for (const key of OAUTH_RESPONSE_KEYS) {
    if (params.getAll(key).length > 1) {
      throw new Error("duplicate OAuth response parameter");
    }
  }
  return rawQuery;
}

function exactHttpsOrigin(value: string): URL {
  const url = new URL(value);
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    url.pathname !== "/" ||
    url.search ||
    url.hash ||
    url.origin !== value
  ) {
    throw new Error("invalid HTTPS origin");
  }
  return url;
}

function exactHttpsEndpoint(value: string): URL {
  const url = new URL(value);
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    url.pathname === "/" ||
    url.search ||
    url.hash ||
    `${url.origin}${url.pathname}` !== value
  ) {
    throw new Error("invalid HTTPS endpoint");
  }
  return url;
}

function validateHostPrefix(prefix: string): void {
  if (prefix.length > 62 || !/^[a-z0-9][a-z0-9-]*$/.test(prefix)) {
    throw invalidRelayConfig();
  }
}

function validateHostSuffix(suffix: string): void {
  if (
    !suffix.startsWith(".") ||
    suffix !== suffix.toLowerCase() ||
    suffix.includes("..")
  ) {
    throw invalidRelayConfig();
  }
  const labels = suffix.slice(1).split(".");
  if (labels.length < 2 || labels.some((label) => !dnsLabel(label))) {
    throw invalidRelayConfig();
  }
}

function dnsLabel(value: string): boolean {
  return (
    value.length >= 1 &&
    value.length <= 63 &&
    /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(value)
  );
}

function requiredString(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) {
    throw invalidRelayConfig();
  }
  return value.trim();
}

function encodeBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function decodeBase64Url(value: string): string {
  if (!value || !/^[A-Za-z0-9_-]+$/u.test(value)) {
    throw new Error("invalid base64url value");
  }
  const standard = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = standard.padEnd(Math.ceil(standard.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}

function invalidRelayConfig(): Error {
  return new Error("Preview auth relay configuration is invalid");
}
